// Shared theme-cycle helpers used by both ThemeToggle (compact icon
// button) and AppHeader.ThemeItem (verbose menu row). Single source of
// truth for `auto → light → dark → auto` ordering and the side-effects:
// reactive state update + localStorage persistence. The framework's
// `changeGlobalTheme` (registered via shared/functions/index.js) is NOT
// called here — the caller invokes it via `el.call('changeGlobalTheme', mode)`
// so this helper stays a plain function with no DOMQL dependency.
//
// Why two surfaces share this: previously ThemeToggle.js and
// AppHeader.ThemeItem each open-coded the same `MODES.indexOf(current) + 1`
// arithmetic + the same localStorage write. Drift was already starting —
// ThemeItem used `s.root.themeMode = next` (direct assignment), ThemeToggle
// did the same but also wrote to localStorage twice. Centralising kills
// the drift.

import { cycleThemeMode, writeStoredThemeMode } from './themeMode.js'

// Cycle a `root.themeMode` value one step forward and persist. Returns
// the new mode so callers can pass it to `changeGlobalTheme`.
//
// Side effects:
//   1. Sets `root.themeMode = next` (reactive — labels/icons re-render).
//   2. Writes `localStorage.themeMode` (cross-tab sync via storage event,
//      and bootShell reads this on next reload).
//
// Caller is responsible for invoking `el.call('changeGlobalTheme', next)`
// after this returns — that's the framework API that flips the
// document's `data-theme` attribute and runs the design-system
// reprocessor.
export const cycleTheme = (root) => {
  const next = cycleThemeMode(root?.themeMode)
  if (root) root.themeMode = next
  return writeStoredThemeMode(next)
}

// Human-readable label for a theme mode. Used by AppHeader.ThemeItem
// to render "Theme: auto" / "Theme: light" / "Theme: dark".
//
// ELEMENT-BOUND. Both call sites already invoke it through the element
// (`el.call` in ThemeToggle, `this.call` in accountMenuItems), so `this` is
// the element and `this.call('polyglot', …)` resolves. A bare call would
// throw on `this.call` — keep it invoked through an element everywhere,
// tests included. The three mode keys are written out rather than built by
// concatenation (docs/I18N-CONVENTION.md §1.3).
export const labelForTheme = function (mode) {
  const m = mode || 'auto'
  const name =
    m === 'light'
      ? this.call('polyglot', 'shell.theme.mode.light')
      : m === 'dark'
        ? this.call('polyglot', 'shell.theme.mode.dark')
        : this.call('polyglot', 'shell.theme.mode.auto')
  return this.call('polyglot', 'shell.theme.label').replace('{mode}', name)
}

// Icon glyph name for a theme mode. Used by ThemeToggle's icon.
export const iconForTheme = (mode) => {
  if (mode === 'light') return 'sun'
  if (mode === 'dark') return 'moon'
  return 'theme' // auto
}
